import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  Paper,
  Switch,
  IconButton,
  InputAdornment,
  Autocomplete,
  CircularProgress,
  Chip,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useSelector } from "react-redux";
import { RootState } from "../../../../store/Index";
import apiService from "../../../../service/Api";
import EventSelector from "./StreamSelector";
import {
  showErrorToast,
  showSuccessToast,
} from "../../../../components/common/ShowToast";
import { IStreams, Stream } from "../../../../types/streams";
import useDebounce from "../../../../hooks/useDebounce";
import CustomTextField from "../../../../components/common/Textfield";

interface Props {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: {
    streamId: number | "";
    vimeo_stream_link: string;
    password_protected: boolean;
    vimeo_download_password?: string;
  }) => void;
  streams?: IStreams[] | null;
  title: string;
  initialSelectedStream?: number | "";
}

export default function StreamDialog({
  open,
  onClose,
  onSubmit,
  streams,
  title,
  initialSelectedStream,
}: Props) {
  const [streamOptions, setStreamOptions] = useState<IStreams[]>([]);
  const [streamValue, setStreamValue] = useState("");
  const [streamsLoading, setStreamsLoading] = useState(false);

  const debouncedStreamSearch = useDebounce(streamValue, 400);
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const [selectedStreams, setSelectedStreams] = useState<IStreams | null>();
  const [selectedStream, setSelectedStream] = useState<number | "">(
    initialSelectedStream || ""
  );
  const [vimeoStreamLink, setVimeoStreamLink] = useState("");
  const [password_protected, setpassword_protected] = useState(false);
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (streams && initialSelectedStream) {
      setSelectedStreams(
        streams?.find((s) => parseInt(s.id) === initialSelectedStream)
      );
    }
  }, [streams, initialSelectedStream]);

  const fetchStreams = useCallback(async (streamSearching: string) => {
    if (!streamSearching) {
      setStreamOptions([]);
      return;
    }

    setStreamsLoading(true);
    try {
      const streams = await apiService.get<{ results: IStreams[] }>(
        "/api/streams/stream/",
        {
          search: streamSearching,
          page_size: 10,
        }
      );
      setStreamOptions(streams.data.results);
    } catch (err) {
      console.error("Failed to fetch streams:", err);
      setStreamOptions([]);
    } finally {
      setStreamsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStreams(debouncedStreamSearch);
  }, [debouncedStreamSearch, fetchStreams]);

  const modalTitle = initialSelectedStream ? "Add Vimeo Link" : title;

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: 2,
      bgcolor: reduxTheme.primary_colors?.[800],
    },
  };

  const handleSubmit = async () => {
    console.log();
    console.log({ selectedStreams });
    console.log({ vimeoStreamLink });
    console.log();
    if (!selectedStreams || !vimeoStreamLink) {
      showErrorToast(
        "Please select a stream and provide a Vimeo link",
        "stream-submit-error"
      );
      return;
    }

    if (password_protected && !password.trim()) {
      showErrorToast(
        "Please provide a password for protected streams",
        "stream-submit-error"
      );
      return;
    }

    setSubmitting(true);
    try {
      await apiService.patch(`/api/streams/stream/${selectedStreams.id}/`, {
        vimeo_stream_link: vimeoStreamLink.trim(),
        vimeo_download_password: password_protected ? password : null,
      });

      showSuccessToast("Stream updated successfully");

      onSubmit({
        streamId: parseInt(selectedStreams.id),
        vimeo_stream_link: vimeoStreamLink,
        password_protected,
        vimeo_download_password: password_protected ? password : undefined,
      });

      handleClose();
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.detail || "Failed to update stream";
      showErrorToast(errorMessage, "stream-submit-error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setSelectedStream("");
    setVimeoStreamLink("");
    setpassword_protected(false);
    setPassword("");
    setShowPassword(false);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      PaperProps={{ sx: { borderRadius: 3 } }}
      scroll="body"
    >
      <DialogTitle sx={{ fontWeight: "bold", fontSize: "1.25rem" }}>
        {modalTitle}
      </DialogTitle>
      <DialogContent>
        <Autocomplete
          multiple={false}
          options={streamOptions}
          getOptionLabel={(option) => {
            const stream = option?.title
              ? option.title
              : "No Streams To Display";
            return stream;
          }}
          value={selectedStreams}
          onChange={(_, newValue) => setSelectedStreams(newValue)}
          inputValue={streamValue}
          onInputChange={(_, newInput) => setStreamValue(newInput)}
          loading={streamsLoading}
          renderInput={(params) => (
            <CustomTextField
              {...params}
              placeholder="Search streams..."
              variant="outlined"
              helperText="Type 3 letters to search"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {streamsLoading ? <CircularProgress size={18} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => {
              const stream = option?.title
                ? option.title
                : "No Streams To Display";
              return (
                <Chip
                  label={stream}
                  {...getTagProps({ index })}
                  key={option.id}
                  sx={{
                    backgroundColor: reduxTheme.primary_colors[800],
                    borderRadius: 1,
                    color: reduxTheme.primary_colors[100],
                  }}
                />
              );
            })
          }
          sx={{ minWidth: 300 }}
        />
        {selectedStreams && (
          <Paper
            sx={{
              p: 2,
              mt: 1,
              mb: 1,
              boxShadow: 0,
              borderColor: reduxTheme.primary_colors?.[800],
              display: "flex",
            }}
          >
            <Box sx={{ flex: 1 }} />
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                <Typography fontWeight="500">
                  {selectedStreams.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {selectedStreams.clientName}
                </Typography>
                <Typography variant="body2">
                  {selectedStreams.over_due_by}
                </Typography>
              </Box>
            </Box>
          </Paper>
        )}

        <Typography variant="subtitle1" sx={{ fontWeight: 500, mb: 0, mt: 2 }}>
          Paste Vimeo Link
        </Typography>
        <TextField
          fullWidth
          margin="dense"
          size="small"
          placeholder="https://vimeo.com/..."
          value={vimeoStreamLink}
          onChange={(e) => setVimeoStreamLink(e.target.value)}
          sx={inputSx}
          disabled={submitting}
        />

        <Typography variant="subtitle1" sx={{ mb: 0, mt: 2 }}>
          Password Protection
        </Typography>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 1 }}
        >
          <Typography variant="body2" color="text.secondary">
            Require a password to view this content
          </Typography>
          <Switch
            checked={password_protected}
            onChange={(e) => setpassword_protected(e.target.checked)}
            disabled={submitting}
          />
        </Box>

        {password_protected && (
          <>
            <Typography variant="subtitle1" sx={{ mb: 0, mt: 2 }}>
              Enter Password
            </Typography>
            <TextField
              fullWidth
              margin="dense"
              size="small"
              type={showPassword ? "text" : "password"}
              placeholder="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              sx={inputSx}
              disabled={submitting}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button
          onClick={onClose}
          variant="outlined"
          size="small"
          disabled={submitting}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          size="small"
          disabled={submitting}
        >
          {submitting ? "Publishing..." : "Publish"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
